<template>
  <div class="referencesGrid">
    <p
      v-for="(reference, arrayIndex) in processedReferences"
      :key="reference.paragraphIndex"
      @click="onDotClick(reference, arrayIndex)"
      class="referenceDot"
      :class="{
        active:
          referenceStore.selectedReference?.renderId === renderId &&
          referenceStore.selectedReference?.index === arrayIndex
      }"
    >
      {{ arrayIndex + 1 }}
    </p>
    <CustomModal
      :show="showModal"
      :closeOnOutsideClick="true"
      @update:show="(val: true) => (showModal = val)"
    >
      <OriginalReference :original-paragraphs="paragraphsConents" />
      <template #left-button>
        <FormButton
          :label="$t('reviewing.reference_dots.re_start_conversation')"
          @click="
            () => {
              onResetReview();
              showModal = false;
            }
          "
        />
      </template>
    </CustomModal>
  </div>
</template>
<script setup lang="ts">
import FormButton from '@/components/common/button/FormButton.vue';
import CustomModal from '@/components/common/modal/CustomModal.vue';
import OriginalReference from '@/components/common/original-reference/OriginalReference.vue';
import { Reference } from '@/models/doc-review';
import { useReferenceSelectionStore } from '@/stores/useReferenceSelectionStore';
import { OfficeUtils } from '@/utils/office.utils';
import { v4 as uuidv4 } from 'uuid';
import { computed, ref } from 'vue';

interface Props {
  referencedParagraphs: Reference[];
  onResetReview: () => void;
  renderId: string;
}

interface ProcessedReference {
  paragraphIndex: number;
  endParagraphIndex?: number;
  paragraphsConents: string[];
}

const props = defineProps<Props>();

const paragraphsConents = ref<Record<string, string>>({});
const showModal = ref(false);
const referenceStore = useReferenceSelectionStore();

const processedReferences = computed<ProcessedReference[]>(() => {
  if (!props.referencedParagraphs || props.referencedParagraphs.length === 0) {
    return [];
  }

  const sorted = [...props.referencedParagraphs].sort(
    (a, b) => a.paragraphIndex - b.paragraphIndex
  );

  const result: ProcessedReference[] = [];
  let group: ProcessedReference | null = null;

  for (const refItem of sorted) {
    if (!group) {
      group = {
        paragraphIndex: refItem.paragraphIndex,
        paragraphsConents: [refItem.paragraphContent]
      };
    } else {
      const lastIndex: number | undefined =
        group.endParagraphIndex ?? group.paragraphIndex;
      if (refItem.paragraphIndex === lastIndex + 1) {
        group.paragraphsConents.push(refItem.paragraphContent);
        group.endParagraphIndex = refItem.paragraphIndex;
      } else {
        result.push(group);
        group = {
          paragraphIndex: refItem.paragraphIndex,
          paragraphsConents: [refItem.paragraphContent]
        };
      }
    }
  }
  if (group) result.push(group);

  return result;
});

const onDotClick = async (
  reference: ProcessedReference,
  arrayIndex: number
) => {
  referenceStore.selectReference(
    props.renderId,
    arrayIndex,
    reference.paragraphsConents
  );
  const paragraphs = await OfficeUtils.getDocumentParagraphs();
  paragraphsConents.value = {};

  const start = reference.paragraphIndex;
  const end = reference.endParagraphIndex ?? reference.paragraphIndex;
  const documentParagraphs: string[] = [];
  for (let i = start; i <= end; i++) {
    documentParagraphs.push(paragraphs[i]);
  }

  const mismatchedParagraphs = documentParagraphs.filter(
    (p) => !reference.paragraphsConents.includes(p)
  );

  if (mismatchedParagraphs.length) {
    mismatchedParagraphs.forEach((paragraph) => {
      paragraphsConents.value[uuidv4()] = paragraph;
    });
    showModal.value = true;
  } else {
    OfficeUtils.scrollToAndSelectParagraph(start, end);
  }
};
</script>
<style lang="scss" scoped>
.referenceDot {
  color: $color-neutral-400;
  padding: $space-1;
  border-radius: $radius-3;
  border: 1px solid $color-neutral-400;
  user-select: none;
  line-height: $body-s-line-height;
  font-size: $body-s-font-size;
  height: $space-5;
  width: $space-5;
  text-align: center;
  box-sizing: content-box;

  &:hover {
    cursor: pointer;
    transform: translateY(-2px) scale(1.05);
    transition: transform 0.15s ease;
  }

  &.active {
    background-color: $color-neutral-400;
    color: $color-text-white;
  }
}

.referencesGrid {
  display: flex;
  flex-wrap: wrap;
  gap: $space-1;
  margin-top: $space-1;
}
</style>
